import type { ReactNode } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';

/**
 * Tarjeta de gráfico: una tarjeta con título y los cuatro estados que TIENE todo bloque de reportes.
 *
 * Existe porque en esta pantalla se repite cinco veces la misma secuencia y equivocarse en una es
 * seguro: cargando → error → vacío → datos. Acá vive una sola vez:
 *
 *  - `loading` (la primera carga) pinta el esqueleto del mismo alto que el gráfico, así el panel no
 *    salta; cuando ya hay datos y solo se está cambiando un filtro NO se usa: de eso se encarga
 *    `keepPreviousData` de las queries, que deja el gráfico anterior en pantalla;
 *  - `error` muestra el mensaje que ya tradujo `getErrorMessage` y un botón para reintentar (el
 *    `refetch` de la query): un panel en blanco por un 500 no le dice nada al admin;
 *  - `empty` es «el periodo no tiene datos», que NO es lo mismo que «todavía no cargó» ni que «falló»;
 *  - `children` son el gráfico o la tabla, con los datos ya cargados.
 *
 * `height` es alto en px del área de datos (gráfico o esqueleto) y sirve para reservar el espacio en
 * los tres estados de arriba: sin él, el panel entero se reacomoda cada vez que llega una respuesta.
 */

export interface ChartCardEmpty {
  title: string;
  description?: string;
  action?: ReactNode;
}

export interface ChartCardProps {
  title: string;
  description?: ReactNode;
  /** Controles de la tarjeta (selector de métrica, intervalo, «ver todos»…). */
  actions?: ReactNode;
  /** Leyenda pegada al título (ver `ChartLegend`). */
  legend?: ReactNode;
  loading?: boolean;
  /** Mensaje YA traducido (`getErrorMessage`). */
  error?: string | null;
  onRetry?: () => void;
  empty?: ChartCardEmpty | null;
  /** Alto del área de datos, en px. */
  height?: number;
  bodyClassName?: string;
  className?: string;
  children: ReactNode;
}

export function ChartCard({
  title,
  description,
  actions,
  legend,
  loading = false,
  error = null,
  onRetry,
  empty = null,
  height,
  bodyClassName,
  className,
  children,
}: ChartCardProps) {
  /*
   * El error y el vacío se pintan DENTRO del cuerpo, no en lugar de la tarjeta: así el título y los
   * controles siguen a la vista (el admin ve qué bloque falló y puede reintentar solo ese).
   */
  let cuerpo: ReactNode = children;

  if (loading) {
    cuerpo = <ChartAreaSkeleton height={height} />;
  } else if (error) {
    cuerpo = (
      <div className="space-y-3">
        <Alert>{error}</Alert>
        {onRetry ? (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Volver a intentar
          </Button>
        ) : null}
      </div>
    );
  } else if (empty) {
    cuerpo = (
      <EmptyState
        title={empty.title}
        description={empty.description}
        action={empty.action}
        className="border-0 bg-transparent px-0 py-6"
      />
    );
  }

  return (
    <Card className={className}>
      <CardHeader actions={actions}>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
        {legend ? <div className="mt-3">{legend}</div> : null}
      </CardHeader>

      <CardContent className={cn('pt-5', bodyClassName)}>{cuerpo}</CardContent>
    </Card>
  );
}

/** Bloque gris del mismo alto que el área de datos (primera carga de un gráfico). */
export function ChartAreaSkeleton({ height = 280 }: { height?: number }) {
  return (
    <div className="flex flex-col justify-end gap-2" style={{ height }} aria-hidden="true">
      <span className="flex items-center gap-2 text-caption text-ink-faint">
        <Spinner className="size-3.5" />
        Cargando…
      </span>
      <span className="block h-3/4 w-full animate-pulse rounded-panel bg-surface-muted" />
    </div>
  );
}

export interface ChartLegendItem {
  /** Nombre de la serie. */
  label: string;
  /** Color de la muestra (`chart.series[i]` o un token). */
  color: string;
  /** Valor actual (ej. el total de esa serie), opcional. */
  valor?: string;
}

/**
 * Leyenda de un gráfico: puntito de color + nombre (+ valor).
 *
 * Los nombres van en el encabezado y no dentro del gráfico porque recharts dibuja la leyenda con
 * tipografía propia y en móvil se come el alto del área de datos.
 */
export function ChartLegend({ items, className }: { items: ChartLegendItem[]; className?: string }) {
  return (
    <ul className={cn('flex flex-wrap items-center gap-x-4 gap-y-1.5', className)}>
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-2 text-caption text-ink-muted">
          <span
            className="size-2.5 shrink-0 rounded-pill"
            style={{ backgroundColor: item.color }}
            aria-hidden="true"
          />
          <span>{item.label}</span>
          {item.valor ? (
            <span className="tabular font-semibold text-ink-soft">{item.valor}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
